import { useState, useEffect, useRef } from "react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

// Configure pdf.js worker
// Make sure to copy the worker file to your public directory
// You might need to adjust the path depending on your build setup
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PdfEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  onSave: (newFile: File) => void;
}

export function PdfEditModal({
  isOpen,
  onClose,
  file,
  onSave,
}: PdfEditModalProps) {
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pagesToRemove, setPagesToRemove] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailUrls, setThumbnailUrls] = useState<string[]>([]); // State for thumbnail URLs
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (isOpen && file) {
      setIsLoading(true);
      setThumbnailsLoading(true);
      setError(null);
      setPagesToRemove(new Set()); // Reset selection on open
      setThumbnailUrls([]); // Reset thumbnails

      const loadPdf = async () => {
        let loadedPdfLibDoc: PDFDocument | null = null;
        let loadedPdfJsDoc: pdfjsLib.PDFDocumentProxy | null = null;
        try {
          const arrayBuffer = await file.arrayBuffer();

          loadedPdfLibDoc = await PDFDocument.load(arrayBuffer.slice(0));
          setPdfDoc(loadedPdfLibDoc);
          const numPages = loadedPdfLibDoc.getPageCount();
          setTotalPages(numPages);
          setIsLoading(false);

          loadedPdfJsDoc = await pdfjsLib.getDocument({ data: arrayBuffer })
            .promise;
          const thumbs: string[] = [];
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            throw new Error("Could not get canvas context");
          }

          for (let i = 1; i <= numPages; i++) {
            try {
              const page = await loadedPdfJsDoc.getPage(i);
              const viewport = page.getViewport({ scale: 0.4 }); // Increased scale for larger preview
              canvas.height = viewport.height;
              canvas.width = viewport.width;

              const renderContext = {
                canvasContext: ctx,
                viewport: viewport,
              };
              await page.render(renderContext).promise;
              thumbs.push(canvas.toDataURL());
            } catch (renderError) {
              console.error(
                `Failed to render thumbnail for page ${i}:`,
                renderError
              );
              thumbs.push(""); // Add empty string or a placeholder URL on error
            }
          }
          setThumbnailUrls(thumbs);
          canvas.remove();
        } catch (err) {
          console.error("Failed to load or render PDF:", err);
          setError("Failed to load PDF. It might be corrupted or protected.");
          toast.error("Failed to load PDF for editing.");
          if (loadedPdfLibDoc) setPdfDoc(null);
          if (loadedPdfJsDoc) loadedPdfJsDoc.destroy();
          onCloseRef.current();
        } finally {
          if (loadedPdfJsDoc && !thumbnailUrls.length && !error) {
            loadedPdfJsDoc.destroy();
          }
          setIsLoading(false); // Ensure main loading is false
          setThumbnailsLoading(false);
          setPagesToRemove(new Set());
          setError(null);
        }
      };
      loadPdf();
    } else {
      // Reset state when modal is closed or no file
      setPdfDoc(null);
      setTotalPages(0);
      setPagesToRemove(new Set());
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, file]);

  const handleRemovePageClick = (pageIndex: number) => {
    setPagesToRemove((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pageIndex)) {
        // Page is already marked, deselect it
        newSet.delete(pageIndex);
      } else {
        // Page is not marked, try to select it for removal
        // Check if removing this page would leave less than 1 page
        if (totalPages - newSet.size <= 1) {
          toast.warning("Cannot remove the last page.");
          return prev; // Return previous state without changes
        }
        newSet.add(pageIndex);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!pdfDoc || !file) return;

    if (pagesToRemove.size === 0) {
      toast.info("No pages selected for removal.");
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      const pagesToKeep = Array.from({ length: totalPages })
        .map((_, i) => i)
        .filter((i) => !pagesToRemove.has(i));

      if (pagesToKeep.length === 0) {
        // This should be prevented by the checkbox logic, but double-check
        toast.error("Cannot remove all pages.");
        setIsLoading(false);
        return;
      }

      // Create a new document and copy the pages we want to keep
      const newPdfDoc = await PDFDocument.create();
      const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToKeep);
      copiedPages.forEach((page) => newPdfDoc.addPage(page));

      const pdfBytes = await newPdfDoc.save();
      const newBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const newFile = new File([newBlob], file.name, {
        type: "application/pdf",
      });

      onSave(newFile);
      toast.success(
        `${pagesToRemove.size} page${
          pagesToRemove.size > 1 ? "s" : ""
        } removed successfully.`
      );
      onClose();
    } catch (err) {
      console.error("Failed to modify PDF:", err);
      setError("Failed to modify PDF.");
      toast.error("An error occurred while editing the PDF.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !file) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg md:max-w-2xl lg:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit PDF: {file.name}</DialogTitle>
          <DialogDescription>
            DELETE pages that do not contain a transcript. For multi-page
            transcripts, use this feature to upload different pages as a new
            file for batch processing.
          </DialogDescription>
        </DialogHeader>
        {isLoading && <p>Loading PDF data...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {!isLoading && !error && pdfDoc && totalPages > 0 && (
          <ScrollArea className="h-[500px] w-full rounded-md border p-4 my-4">
            {thumbnailsLoading && <p>Loading thumbnails...</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: totalPages }).map((_, index) => {
                const isMarkedForRemoval = pagesToRemove.has(index);
                // Disable button only if it's NOT marked AND removing it would leave 0 or 1 pages.
                const disableButton =
                  !isMarkedForRemoval && totalPages - pagesToRemove.size <= 1;

                return (
                  <div
                    key={index}
                    className={`flex flex-col items-center space-y-2 border p-2 rounded-md transition-opacity ${
                      isMarkedForRemoval
                        ? "opacity-50 border-red-500"
                        : "opacity-100" // More visual feedback
                    }`}
                  >
                    <label
                      htmlFor={`page-${index}-button`}
                      className={`text-sm font-medium leading-none mb-1 ${
                        disableButton ? "text-gray-400" : "" // Dim label if button is disabled
                      }`}
                    >
                      Page {index + 1}
                    </label>
                    {thumbnailUrls[index] ? (
                      <img
                        src={thumbnailUrls[index]}
                        alt={`Page ${index + 1} thumbnail`}
                        className="h-40 w-auto border border-gray-300 object-contain mb-2"
                      />
                    ) : thumbnailsLoading ? (
                      <div className="h-40 w-32 bg-gray-200 animate-pulse rounded mb-2"></div>
                    ) : (
                      <div className="h-40 w-32 bg-gray-100 border border-gray-300 flex items-center justify-center text-xs text-gray-500 mb-2">
                        Preview N/A
                      </div>
                    )}
                    <Button
                      id={`page-${index}-button`}
                      variant={isMarkedForRemoval ? "secondary" : "destructive"} // Change variant when marked
                      size="sm"
                      onClick={() => handleRemovePageClick(index)}
                      disabled={disableButton} // Use the calculated disabled state
                      className="w-full"
                    >
                      {isMarkedForRemoval ? "Undo Delete" : "Delete"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isLoading ||
              pagesToRemove.size === 0 ||
              totalPages - pagesToRemove.size < 1
            }
          >
            {isLoading
              ? "Saving..."
              : `Save PDF (${totalPages - pagesToRemove.size} page${
                  totalPages - pagesToRemove.size !== 1 ? "s" : ""
                })`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
